import React, { useState, useRef, useEffect } from "react"
import Task from "../../../../../../model2/task/task"
import { FieldTableCellProps, getTableCellStyle } from "../../../../../render/table/table-cell"
import { isBulkSelection } from "../../../../../../gd/right-click-menu"
import ApiTask from "../../../../../../gd/api/task"
import ApiBulkActions from "../../../../../../gd/api/bulk-actions"
import FieldTaskUsers_PopoverMenu from "./popover-menu"
import FieldTaskUsers_CellIcons from "./cell-icons"

export default function FieldTaskUsers_TableCell(props: FieldTableCellProps) {
    const task: Task = props.listItem.task as Task

    const [isHovered, setIsHovered] = useState<boolean>(false)
    const [showPopover, setShowPopover] = useState<boolean>(false)
    const [currentOptions, setCurrentOptions] = useState<ID[]>(() => {
        return _.map(task.users, (tu: any) => {
            return tu.userId
        })
    })
    const containerRef          = useRef<HTMLDivElement>(null)
    const baseUsersSnapshotRef  = useRef<any[] | null>(null)

    const debouncedApiCallRef = useRef(_.debounce(async (finalIds: ID[], taskId: ID, protectedIds: ID[]) => {
        if (!baseUsersSnapshotRef.current) {
            return
        }
        const baseUserIds   = _.map(baseUsersSnapshotRef.current, (tu: any) => tu.userId)
        const added         = _.difference(finalIds, baseUserIds).filter((id: ID) => !protectedIds.includes(id))
        const removed       = _.difference(baseUserIds, finalIds).filter((id: ID) => !protectedIds.includes(id))
        baseUsersSnapshotRef.current = null

        if (added.length > 0) {
            ApiTask.addUsers2(
                taskId,
                added.map((userId: ID) => ({
                    userId  : userId,
                    type    : 'project'
                }))
            )
        }

        if (removed.length > 0) {
            ApiBulkActions.removeUsersFromTasks([taskId], removed)
        }

        /*for (const userId of removed) {
            await ApiTask.userDelete2(taskId, userId).promise()
        }*/
    }, 1000))

    useEffect(() => {
        return () => {
            debouncedApiCallRef.current.flush()
        }
    }, [])

    const canEdit: boolean = (!props.manager.isReadOnly) && props.field.canEdit(props.listItem.item)

    const getProtectedUserIds = (): ID[] => {
        return _.compact(
            _.uniq([
                task.actionRequiredUserId,
                task.assignedToUserId
            ])
        )
    }

    const hidePopover = () => {
        setShowPopover(false)
        setIsHovered(false)
        return
    }

    const onMouseEnter = () => {
        if (!canEdit) {
            return
        }
        if (isHovered) {
            return
        }
        setIsHovered(true)
        return
    }

    const onMouseLeave = () => {
        if (showPopover) {
            return
        }
        if (!isHovered) {
            return
        }
        setIsHovered(false)
        return
    }

    const onClick = (e: React.MouseEvent) => {
        if (isBulkSelection(e)) {
            return
        }
        if (!canEdit) {
            return
        }
        if (!showPopover) {
            setCurrentOptions(_.map(task.users, (tu: any) => tu.userId))
        }
        setShowPopover((prev: boolean) => {
            return !prev
        })
        return
    }

    const onOptionClick = (option: Option, e: React.MouseEvent) => {
        if (isBulkSelection(e)) {
            return
        }
        const protectedIds: ID[] = getProtectedUserIds()
        if (protectedIds.includes(option.value)) {
            return
        }
        let newIds: ID[]
        if (currentOptions.includes(option.value)) {
            newIds = currentOptions.filter((id: ID) => {
                return id !== option.value
            })
        } else {
            newIds = [
                ...currentOptions,
                option.value
            ]
        }
        onBulkChange(newIds)
        return
    }

    const onBulkChange = (newIds: ID[]) => {
        const protectedIds: ID[]    = getProtectedUserIds()
        const finalIds: ID[]        = _.union(newIds, protectedIds)
        const taskUserIds: ID[]     = _.map(task.users, (tu: any) => tu.userId)
        const added: ID[]           = _.difference(finalIds, taskUserIds).filter((id: ID) => {
            return !protectedIds.includes(id)
        })
        const removed: ID[]         = _.difference(taskUserIds, finalIds).filter((id: ID) => {
            return !protectedIds.includes(id)
        })

        setCurrentOptions(finalIds)

        if (!baseUsersSnapshotRef.current) {
            baseUsersSnapshotRef.current = [...task.users]
        }

        _.each(added, (userId: ID) => {
            if (!_.find(task.users, (tu: any) => {
                return tu.userId === userId
            })) {
                task.users.push({
                    userId: userId
                })
            }
            return
        })
        _.each(removed, (userId: ID) => {
            task.users = _.filter(task.users, (tu: any) => {
                return tu.userId !== userId
            })
            return
        })

        debouncedApiCallRef.current(finalIds, task.id, protectedIds)
        return
    }

    if (isHovered) {
        return (
            <div ref            = {containerRef}
                 className      = "cell-flex is-hovered ui5-txt-less-important-small"
                 style          = {getTableCellStyle(props)}
                 onMouseEnter   = {onMouseEnter}
                 onMouseLeave   = {onMouseLeave}
                 onClick        = {onClick}
            >
                <div className="ui5-field-editable is-hovered size-small w-100-percent h-100-percent">
                    <div className="ui5-users-group txt-truncate">
                        <FieldTaskUsers_CellIcons
                            task        = {task}
                            colWidth    = {props.column.width}
                        />
                    </div>
                    <div className="buttons flex-no-shrink">
                        <div className="btn-arrow"/>
                    </div>
                </div>
                {showPopover && (
                    <FieldTaskUsers_PopoverMenu
                        parentRef       = {containerRef}
                        currentOptions  = {currentOptions}
                        field           = {props.field}
                        model           = {task as any}
                        onOptionClick   = {onOptionClick}
                        onBulkChange    = {onBulkChange}
                        hidePopover     = {hidePopover}
                    />
                )}
            </div>
        )
    }

    return (
        <div className      = "cell-flex is-hovered ui5-txt-less-important-small"
             style          = {getTableCellStyle(props)}
             onMouseEnter   = {onMouseEnter}
             onMouseLeave   = {onMouseLeave}
        >
            <div className="ui5-field-editable size-small w-100-percent h-100-percent">
                <div className="ui5-users-group txt-truncate">
                    <FieldTaskUsers_CellIcons
                        task        = {task}
                        colWidth    = {props.column.width}
                    />
                </div>
            </div>
        </div>
    )
}
